/**
 * What a share link opens — `/s/<token>` (FR-29, plan §5.3), **outside the
 * session guard**: the person opening it may have no FiveOX account at all.
 *
 * The token is in this page's address and **nowhere else**: every read sends
 * it as `X-Share-Token` (and a passcode as `X-Share-Passcode`), never in an
 * API path or query (D26), and the edge logs no `/s/` request (D35). The
 * service decides on every read: a link that is unknown, expired or revoked is
 * one `NOT_FOUND`; a passcode is asked for, and ten wrong ones lock it for
 * fifteen minutes; an `access: org` link asks the reader to sign in. A folder
 * link is a read-only listing to browse, with each file downloaded on its own —
 * one logged read per file (`link`, `share`).
 *
 * The passcode is held in this page's memory while it is open, so a folder can
 * be browsed without asking again — never stored, never put in an address.
 */

import {
  ApiError,
  FileMark,
  createShareLinkClient,
  documentTypeLabel,
  errorText,
  formatBytes,
  linkRefusalOf,
  mediaLabel,
  plural,
  type Fetcher,
  type LinkRefusal,
  type ShareLinkClient,
  type SharedBundle,
  type SharedDocument,
  type SharedFolder,
  type SharedFolderListing,
} from '@fiveox/prodoc-ui'
import { startSsoLogin, stashReturnTo } from '@fiveox/shell'
import { Alert, AppIcon, Button, Card, EmptyState, Input, Logo, Skeleton, SkeletonRegion, formatDate, formatDateTime } from '@fiveox/ui'
import { ChevronRight, Download, Folder as FolderIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { SERVICE_API } from '../lib/config'
import { session } from '../lib/session'
import { RETURN_KEY, ssoConfig } from '../lib/sso-config'
import { saveFile } from '../lib/zip'

/**
 * How the page reaches the service. Signed out: the browser's `fetch`, with no
 * credential. Signed in (for an `access: org` link): the bearer — but **not**
 * the shell's refresh-and-replay on a passcode refusal, because a replayed
 * wrong passcode is a second wrong try towards the lockout. Any other 401 is a
 * spent token: refreshed once, then replayed.
 */
export const linkFetcher: Fetcher = async (url, init = {}) => {
  const current = session.getSession()
  if (!current) return fetch(url, init)
  const send = (token: string) => fetch(url, { ...init, headers: { ...((init.headers as Record<string, string> | undefined) ?? {}), authorization: `Bearer ${token}` } })
  const res = await send(current.accessToken)
  if (res.status !== 401) return res
  const reason = await res
    .clone()
    .json()
    .then((b: { error?: { details?: { reason?: unknown } } }) => b?.error?.details?.reason)
    .catch(() => undefined)
  if (reason === 'passcode_required' || reason === 'passcode_wrong') return res
  const refreshed = await session.refreshSession()
  return refreshed ? send(refreshed.accessToken) : res
}

export function SharedLinkPage() {
  const { token = '' } = useParams()
  const signedIn = Boolean(session.useSession())
  const link = useMemo(() => createShareLinkClient({ baseUrl: SERVICE_API, fetcher: linkFetcher }), [])
  return (
    <SharedLinkScreen
      link={link}
      token={token}
      signedIn={signedIn}
      onSignIn={() => {
        // No login screen here (platform ADR-0048): accounts signs the person
        // in, and the SSO callback brings them back to this link.
        stashReturnTo(RETURN_KEY)
        void startSsoLogin(ssoConfig())
      }}
      save={saveFile}
    />
  )
}

/* ------------------------------------------------------------ the screen */

export interface SharedLinkScreenProps {
  link: ShareLinkClient
  token: string
  signedIn: boolean
  onSignIn: () => void
  /** Hand a downloaded file to the browser. */
  save: (blob: Blob, fileName: string) => void
}

type Refused = { kind: LinkRefusal; message: string; lockedUntil: string | null }

export function SharedLinkScreen({ link, token, signedIn, onSignIn, save }: SharedLinkScreenProps) {
  const [passcode, setPasscode] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [bundle, setBundle] = useState<SharedBundle | null>(null)
  const [refused, setRefused] = useState<Refused | null>(null)
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)
  // Inside a folder of the link: its listing, and the way back up.
  const [inside, setInside] = useState<SharedFolderListing | null>(null)
  const [browsing, setBrowsing] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')

  const key = useMemo(() => ({ token, passcode }), [token, passcode])

  const refusal = (e: unknown): Refused => {
    const kind = linkRefusalOf(e)
    const until = e instanceof ApiError && typeof e.details.locked_until === 'string' ? e.details.locked_until : null
    return { kind, message: errorText(e), lockedUntil: until }
  }

  const open = useCallback(
    async (withPasscode: string | null) => {
      setChecking(true)
      try {
        const answer = await link.open({ token, passcode: withPasscode })
        setBundle(answer)
        setRefused(null)
        setPasscode(withPasscode)
      } catch (e) {
        setBundle(null)
        setRefused(refusal(e))
      } finally {
        setChecking(false)
        setLoading(false)
      }
    },
    [link, token],
  )

  useEffect(() => {
    setLoading(true)
    setInside(null)
    void open(null)
    // A sign-in on this page reopens the link with the bearer.
  }, [open, signedIn])

  const browse = async (folder: SharedFolder) => {
    setBrowsing(true)
    setError('')
    try {
      setInside(await link.folder(key, folder.id))
    } catch (e) {
      const r = refusal(e)
      if (r.kind === 'gone' || r.kind === 'locked' || r.kind === 'passcode_required') setRefused(r)
      else setError(r.message)
    } finally {
      setBrowsing(false)
    }
  }

  const download = async (doc: SharedDocument) => {
    setBusy(doc.id)
    setError('')
    try {
      const handle = await link.file(key, doc.id)
      save(await handle.blob(), handle.fileName ?? doc.file_name)
    } catch (e) {
      const r = refusal(e)
      if (r.kind === 'gone' || r.kind === 'locked') setRefused(r)
      else setError(r.message)
    } finally {
      setBusy(null)
    }
  }

  let body
  if (loading) {
    body = (
      <SkeletonRegion label="Opening the link" className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </SkeletonRegion>
    )
  } else if (refused && (refused.kind === 'passcode_required' || refused.kind === 'passcode_wrong')) {
    body = (
      <Card padding="md" className="mx-auto max-w-md space-y-4">
        <div>
          <h1 className="text-foreground text-xl font-semibold">This link needs its passcode</h1>
          <p className="text-muted-foreground mt-1 text-sm">The person who shared it will have told you the passcode separately.</p>
        </div>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (draft.trim() && !checking) void open(draft.trim())
          }}
        >
          <Input
            label="Passcode"
            type="password"
            autoComplete="off"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            error={refused.kind === 'passcode_wrong' ? 'That passcode is not right. After ten wrong tries the link locks for fifteen minutes.' : null}
          />
          <Button type="submit" loading={checking} loadingText="Checking…" disabled={!draft.trim()}>
            Open
          </Button>
        </form>
      </Card>
    )
  } else if (refused?.kind === 'locked') {
    body = (
      <EmptyState
        title="This link is locked for now"
        description={`Too many wrong passcodes were tried, so it is locked for fifteen minutes${refused.lockedUntil ? ` (until ${formatDateTime(refused.lockedUntil)})` : ''}. Try again after that, or ask the person who shared it.`}
      />
    )
  } else if (refused?.kind === 'sign_in_required') {
    body = (
      <EmptyState
        title={signedIn ? 'This link is for another organisation' : 'Sign in to open these documents'}
        description={
          signedIn
            ? 'It opens only for people of the organisation that shared it. If you belong to it, sign in with that account.'
            : 'This link is for people of the organisation that shared it. Sign in with your FiveOX account and it opens here.'
        }
        action={<Button onClick={onSignIn}>{signedIn ? 'Sign in with another account' : 'Sign in'}</Button>}
      />
    )
  } else if (refused?.kind === 'gone') {
    body = <EmptyState title="This link does not open" description="It may have expired or been revoked, or the address is incomplete. Ask the person who shared it for a new one." />
  } else if (refused) {
    body = (
      <EmptyState
        title="This link could not be opened"
        description={refused.message}
        action={
          <Button variant="outline" onClick={() => void open(passcode)}>
            Try again
          </Button>
        }
      />
    )
  } else if (bundle) {
    const documents = inside ? inside.documents : bundle.documents
    const folders = inside ? inside.folders : bundle.folders
    body = (
      <div className="space-y-6">
        <div>
          <p className="text-muted-foreground text-sm">
            {bundle.shared_by && bundle.org_name ? `${bundle.shared_by} at ${bundle.org_name} shared this with you` : bundle.org_name ? `Shared with you by ${bundle.org_name}` : 'Shared with you'}
          </p>
          <h1 className="text-foreground mt-1 text-2xl font-semibold">
            {inside ? inside.folder.name : summaryOf(bundle)}
          </h1>
        </div>

        {inside && (
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-sm">
            <Button variant="link" className="h-auto px-0" onClick={() => setInside(null)}>
              Everything shared
            </Button>
            {inside.breadcrumb.map((c, i) => (
              <span key={c.id} className="flex items-center gap-1">
                <ChevronRight size={13} className="text-muted-foreground" aria-hidden />
                {i === inside.breadcrumb.length - 1 ? (
                  <span className="text-foreground font-medium">{c.name}</span>
                ) : (
                  <Button variant="link" className="h-auto px-0" onClick={() => void browse({ id: c.id, name: c.name, document_count: null, folder_count: null })}>
                    {c.name}
                  </Button>
                )}
              </span>
            ))}
          </nav>
        )}

        {error && <Alert tone="error">{error}</Alert>}

        {browsing ? (
          <SkeletonRegion label="Opening the folder" className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </SkeletonRegion>
        ) : folders.length === 0 && documents.length === 0 ? (
          <EmptyState
            title={inside ? 'This folder is empty' : 'Nothing is in this share any more'}
            description={inside ? 'Nothing is filed here now. What is filed later appears while the link works.' : 'What it reached has been moved or archived since it was shared.'}
          />
        ) : (
          <Card>
            <ul className="divide-border divide-y" aria-label="Shared items">
              {folders.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-5 py-3">
                  <span className="bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" aria-hidden>
                    <FolderIcon size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium">{f.name}</p>
                    {f.document_count !== null && <p className="text-muted-foreground text-xs">{plural(f.document_count, 'document')}</p>}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => void browse(f)} aria-label={`Open the folder ${f.name}`}>
                    Open
                  </Button>
                </li>
              ))}
              {documents.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 px-5 py-3">
                  <FileMark mediaType={doc.media_type} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium" title={doc.title}>
                      {doc.title}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {[doc.title !== doc.file_name ? doc.file_name : null, doc.document_type ? documentTypeLabel(doc.document_type, null) : mediaLabel(doc.media_type), formatBytes(doc.size_bytes)]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => void download(doc)} loading={busy === doc.id} loadingText="…" aria-label={`Download ${doc.title}`}>
                    <Download size={14} aria-hidden />
                    Download
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <p className="text-muted-foreground text-xs">
          {bundle.access === 'org' ? 'For people of the organisation that shared it' : 'For anyone holding this link'}
          {bundle.expires_at ? ` · works until ${formatDate(bundle.expires_at)}` : ''}. Each download is recorded. Shared through FiveOX.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen">
      <header className="border-subtle flex items-center gap-3 border-b bg-white px-4 py-3 sm:px-6">
        <Logo className="h-7" />
        <span className="border-subtle text-foreground inline-flex items-center gap-1.5 rounded-full border py-1 pr-3 pl-1.5 text-sm font-semibold">
          <AppIcon slug="prodoc" name="ProDoc" size={20} />
          ProDoc
        </span>
      </header>
      <main className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">{body}</main>
    </div>
  )
}

/** "Invoice 0431", "3 documents and 1 folder". */
function summaryOf(bundle: SharedBundle): string {
  if (bundle.documents.length === 1 && bundle.folders.length === 0) return bundle.documents[0]!.title
  if (bundle.folders.length === 1 && bundle.documents.length === 0) return bundle.folders[0]!.name
  return [bundle.documents.length ? plural(bundle.documents.length, 'document') : '', bundle.folders.length ? plural(bundle.folders.length, 'folder') : ''].filter(Boolean).join(' and ') || 'Shared documents'
}
